import { ChangeEvent, FC, useMemo, useState } from 'react';
import { SelectField } from '@/components/form/select-field';
import { TextField } from '@/components/form/text-field';
import { Button } from '@/components/ui/button';
import { Modal } from '@/components/ui/modal';
import {
  DeviceFunctionStage,
  useExecuteDeviceFunctionMutation,
} from '@/redux/api/endpoints/device-functions';
import { AlexaFunctionCard } from './alexa-function-card';
import { AlexaFunctionResult } from './alexa-function-result';
import { buildEnableSkillRequest } from './request-builders';
import { KnownAlexaSkill } from './types';

const CUSTOM_SKILL = '__custom__';
const stageOptions = [
  { label: 'Development', value: 'development' },
  { label: 'Live', value: 'live' },
] satisfies { label: string; value: DeviceFunctionStage }[];

type EnableSkillFunctionProps = {
  deviceId: number;
  roomEndpoint?: string;
  skills: KnownAlexaSkill[];
};

const isAlexaSkillStage = (
  stage: string | undefined,
): stage is DeviceFunctionStage => stage === 'development' || stage === 'live';

export const EnableSkillFunction: FC<EnableSkillFunctionProps> = ({
  deviceId,
  roomEndpoint,
  skills,
}) => {
  const [selection, setSelection] = useState(
    skills.length === 0 ? CUSTOM_SKILL : '',
  );
  const [customSkillId, setCustomSkillId] = useState('');
  const [stage, setStage] = useState<DeviceFunctionStage | ''>('');
  const [confirmationOpen, setConfirmationOpen] = useState(false);
  const [execute, { data, error, isLoading }] =
    useExecuteDeviceFunctionMutation();

  const selectedKnownSkill = useMemo(
    () => skills.find((skill) => skill.skillId === selection),
    [selection, skills],
  );
  const skillId =
    selection === CUSTOM_SKILL
      ? customSkillId.trim()
      : (selectedKnownSkill?.skillId ?? '');
  const valid = Boolean(roomEndpoint && skillId && stage);

  const handleSkillChange = (event: ChangeEvent<HTMLSelectElement>) => {
    const nextSelection = event.target.value;
    setSelection(nextSelection);
    const knownSkill = skills.find((skill) => skill.skillId === nextSelection);
    setStage(
      isAlexaSkillStage(knownSkill?.declaredStage)
        ? knownSkill.declaredStage
        : '',
    );
  };

  const confirm = () => {
    if (!roomEndpoint || !skillId || !stage) {
      return;
    }
    setConfirmationOpen(false);
    void execute(buildEnableSkillRequest(deviceId, skillId, stage));
  };

  const skillOptions = [
    ...skills.map((skill) => ({
      value: skill.skillId,
      label: `${skill.skillName ?? skill.skillId}${skill.declaredStage ? ` (${skill.declaredStage})` : ''}`,
    })),
    { value: CUSTOM_SKILL, label: 'Custom skill ID' },
  ];

  return (
    <>
      <AlexaFunctionCard
        description="Enable a known policy skill or supply a custom Alexa skill ID."
        title="Enable skill"
      >
        <div className="flex flex-col gap-3">
          <SelectField
            disabled={!roomEndpoint || isLoading}
            label="Skill"
            onChange={handleSkillChange}
            options={skillOptions}
            value={selection}
          />
          {selection === CUSTOM_SKILL && (
            <TextField
              disabled={!roomEndpoint || isLoading}
              label="Alexa skill ID"
              onChange={(event) => setCustomSkillId(event.target.value)}
              placeholder="amzn1.ask.skill…"
              value={customSkillId}
            />
          )}
          {selection && (
            <SelectField
              disabled={!roomEndpoint || isLoading}
              label="Stage"
              onChange={(event) =>
                setStage(event.target.value as DeviceFunctionStage | '')
              }
              options={stageOptions}
              value={stage}
            />
          )}
          <div>
            <Button
              disabled={!valid || isLoading}
              onClick={() => setConfirmationOpen(true)}
              type="button"
              variant="secondary"
            >
              Enable skill
            </Button>
          </div>
        </div>
        {!roomEndpoint && (
          <p className="mt-2 text-xs text-gray-500">
            This Alexa device has no room endpoint.
          </p>
        )}
        <AlexaFunctionResult data={data} error={error} isLoading={isLoading} />
      </AlexaFunctionCard>
      <Modal
        buttonItems={[
          <Button
            key="cancel"
            onClick={() => setConfirmationOpen(false)}
            variant="secondary"
          >
            Cancel
          </Button>,
          <Button key="confirm" onClick={confirm} variant="primary-warning">
            Enable skill
          </Button>,
        ]}
        description="This sends a live skill-enablement request to Amazon Smart Properties."
        open={confirmationOpen}
        setOpen={setConfirmationOpen}
        title="Confirm skill enablement"
      >
        <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-2 px-6 pb-4 text-sm">
          <dt className="font-medium text-gray-700">Skill</dt>
          <dd className="break-all text-gray-600">{skillId}</dd>
          <dt className="font-medium text-gray-700">Stage</dt>
          <dd className="text-gray-600">{stage}</dd>
          <dt className="font-medium text-gray-700">Room</dt>
          <dd className="break-all text-gray-600">{roomEndpoint}</dd>
        </dl>
      </Modal>
    </>
  );
};
